import { Button } from "@common/components/ui/button"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { PaneNumber } from "@/components/layout/detail-panel"
import { StepStateIcon } from "@/routes/_layout/planning/-components/plan-status"
import { formatDays, formatSpan } from "@/routes/_layout/planning/-lib/days"
import {
  barOf,
  crewFor,
  type Plan,
  partsFor,
  type Step,
  stepState,
  stepStateLabel,
  taskRef,
  taskState,
  tasksOf,
  toolsFor,
} from "@/routes/_layout/planning/-lib/plans"
import { usePlan } from "@/routes/_layout/planning/-lib/store"
import {
  Chip,
  PersonAvatar,
} from "@/routes/_layout/planning_/$planId/-components/chip"
import {
  MaterialRow,
  SheetIntro,
  SheetPanel,
  SheetRow,
  SheetSection,
  SheetTitle,
} from "@/routes/_layout/planning_/$planId/-components/sheet"
import KeyboardArrowDown from "~icons/material-symbols/keyboard-arrow-down"
import KeyboardArrowUp from "~icons/material-symbols/keyboard-arrow-up"

export const Route = createFileRoute(
  "/_layout/planning_/$planId/steps/$stepId/tasks/$taskIndex",
)({
  component: TaskSheet,
})

function TaskSheet(): React.ReactElement | null {
  const { planId, stepId, taskIndex } = Route.useParams()
  const plan = usePlan(planId)
  const navigate = useNavigate()
  const step = plan?.steps.find((each) => each.id === stepId)
  const index = Number(taskIndex)
  const tasks = plan && step ? tasksOf(plan, step) : []
  const task = tasks[index]
  if (!plan || !step || !task) return null

  const close = () =>
    navigate({
      to: "/planning/$planId",
      params: { planId },
      search: true,
    })
  const crew = crewFor(step)
  const person = crew[index % crew.length] ?? ""
  const state = taskState(plan, step, task)
  const ref = taskRef(step, index)
  const neighbour = (offset: number) =>
    (index + offset + tasks.length) % tasks.length

  return (
    <SheetPanel
      title={task.name}
      onClose={close}
      heading={
        <>
          <PaneNumber>{ref}</PaneNumber>
          <span className="flex items-center gap-2 px-2 text-small-regular">
            <StepStateIcon state={state} />
            {stepStateLabel(state)}
          </span>
        </>
      }
      actions={
        <>
          <span className="px-1 text-small-regular text-muted-foreground">
            {index + 1} of {tasks.length}
          </span>
          <TaskLink
            plan={plan}
            step={step}
            index={neighbour(-1)}
            label="Previous task"
          >
            <KeyboardArrowUp />
          </TaskLink>
          <TaskLink
            plan={plan}
            step={step}
            index={neighbour(1)}
            label="Next task"
          >
            <KeyboardArrowDown />
          </TaskLink>
        </>
      }
    >
      <SheetIntro>
        <SheetTitle>{task.name}</SheetTitle>
        <div className="flex flex-wrap gap-1.5">
          <Chip className="h-7 text-small-regular">
            <span className="text-muted-foreground">Department</span>
            {step.shop}
          </Chip>
          <Chip className="h-7 text-small-regular">
            <PersonAvatar name={person} />
            {person}
          </Chip>
          <Chip className="h-7 text-small-regular">
            {formatSpan(task.start, task.end)}
          </Chip>
          <Chip className="h-7 text-small-regular">
            {formatDays(task.end - task.start + 1)}
          </Chip>
        </div>
      </SheetIntro>
      <SheetSection title="Parts" empty="No parts on this task.">
        {partsFor(ref).map((part) => (
          <MaterialRow key={part.number} material={part} />
        ))}
      </SheetSection>
      <SheetSection title="Tools" empty="No special tooling on this task.">
        {toolsFor(ref).map((tool) => (
          <MaterialRow key={tool.number} material={tool} />
        ))}
      </SheetSection>
      <SheetSection title="Part of" empty="None">
        {[<ParentStep key={step.id} plan={plan} step={step} />]}
      </SheetSection>
      <SheetSection title="Other tasks in this step" empty="No other tasks">
        {tasks.flatMap((other, position) =>
          position === index
            ? []
            : [
                <Link
                  key={`${other.name}:${other.start}`}
                  to="/planning/$planId/steps/$stepId/tasks/$taskIndex"
                  params={{ planId, stepId, taskIndex: String(position) }}
                  search={true}
                  className="block hover:no-underline"
                >
                  <SheetRow
                    icon={
                      <StepStateIcon state={taskState(plan, step, other)} />
                    }
                    reference={taskRef(step, position)}
                    name={other.name}
                    detail={formatSpan(other.start, other.end)}
                    people={[crew[position % crew.length] ?? ""]}
                  />
                </Link>,
              ],
        )}
      </SheetSection>
    </SheetPanel>
  )
}

function TaskLink({
  plan,
  step,
  index,
  label,
  children,
}: {
  plan: Plan
  step: Step
  index: number
  label: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <Button
      asChild
      variant="ghost"
      size="icon-sm"
      className="text-muted-foreground"
      aria-label={label}
      title={label}
    >
      <Link
        to="/planning/$planId/steps/$stepId/tasks/$taskIndex"
        params={{ planId: plan.id, stepId: step.id, taskIndex: String(index) }}
        search={true}
      >
        {children}
      </Link>
    </Button>
  )
}

function ParentStep({
  plan,
  step,
}: {
  plan: Plan
  step: Step
}): React.ReactElement {
  const span = barOf(plan, step.id)
  return (
    <Link
      to="/planning/$planId/steps/$stepId"
      params={{ planId: plan.id, stepId: step.id }}
      search={true}
      className="block hover:no-underline"
    >
      <SheetRow
        icon={<StepStateIcon state={stepState(plan, step.id)} />}
        name={step.name}
        detail={formatSpan(span.start, span.end)}
        people={crewFor(step)}
      />
    </Link>
  )
}
